// Derives the dark/light/tint/ink variants tokens.css expects from a single
// admin-picked base color, so a custom brand color still gets a full,
// visually-consistent ramp instead of just overriding one flat swatch.
// Mix ratios are reverse-engineered from tokens.css's own green/amber ramps
// (e.g. --green-dark is roughly --green mixed 30% toward black).
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const value = parseInt(clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return '#' + [r, g, b].map((n) => clamp(n).toString(16).padStart(2, '0')).join('');
}

function mix(hex: string, towards: [number, number, number], ratio: number): string {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHex(
    r + (towards[0] - r) * ratio,
    g + (towards[1] - g) * ratio,
    b + (towards[2] - b) * ratio
  );
}

const BLACK: [number, number, number] = [0, 0, 0];
const WHITE: [number, number, number] = [255, 255, 255];

export function darken(hex: string, ratio = 0.3): string {
  return mix(hex, BLACK, ratio);
}

export function lighten(hex: string, ratio = 0.9): string {
  return mix(hex, WHITE, ratio);
}

// Applies an org's custom brand colors app-wide by overriding the CSS
// custom properties every stylesheet already reads from (tokens.css's
// :root block) — no per-component wiring needed. Ratios are tuned to
// reproduce tokens.css's own default green/amber ramps when fed their
// original base colors (#3f7a29 -> --green-dark #2c591c, etc.).
export function applyThemeColors(primary: string, accent: string): void {
  const root = document.documentElement.style;
  root.setProperty('--green', primary);
  root.setProperty('--green-dark', darken(primary, 0.3));
  root.setProperty('--green-light', lighten(primary, 0.9));
  root.setProperty('--amber', accent);
  root.setProperty('--amber-tint', lighten(accent, 0.92));
  root.setProperty('--amber-ink', darken(accent, 0.4));
}
